/**
 * D-1024-TECHHELPER (part 1) — a small ndt7 client for M-Lab's speed test (locate → WebSocket download → WebSocket
 * upload, about 10 seconds each). Our own, to the ndt7 protocol, rather than the `@m-lab/ndt7` package: that one is a
 * web-worker bundle that needs its worker files served beside the app; this is 100 lines, Vite-clean and testable
 * with a fake WebSocket. The maths and the parsing are in shared/ndt7.ts.
 *
 * Only test traffic leaves the device. The result is never sent anywhere but the app's own kit file.
 */
import { NDT7_LOCATE_URL, NDT7_SUBPROTOCOL, mbps, parseLocate, parseServerMeasurement, uploadMbpsFromServer, type ServerMeasurement } from '@shared/ndt7';

export class Ndt7Unavailable extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'Ndt7Unavailable';
  }
}

export interface Ndt7Result {
  download: number;
  upload: number | null;
  /** MinRTT the server saw, milliseconds. */
  minRtt: number | null;
  site: string | null;
}

export interface Ndt7Options {
  upload: boolean;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  WebSocketImpl?: typeof WebSocket;
  now?: () => number;
  durationMs?: number;
  /** How long to wait for locate and for a socket's first message. */
  connectMs?: number;
}

const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function locate(o: Pick<Ndt7Options, 'signal' | 'fetchImpl' | 'connectMs'>) {
  const ctrl = new AbortController();
  const stop = () => ctrl.abort();
  o.signal?.addEventListener('abort', stop, { once: true });
  const timer = setTimeout(stop, o.connectMs ?? 6000);
  try {
    const r = await (o.fetchImpl ?? fetch)(NDT7_LOCATE_URL, { signal: ctrl.signal, cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer' });
    if (!r.ok) throw new Ndt7Unavailable('locate');
    const target = parseLocate(await r.json());
    if (!target) throw new Ndt7Unavailable('no-server');
    return target;
  } catch (e) {
    if (o.signal?.aborted) throw e;
    throw e instanceof Ndt7Unavailable ? e : new Ndt7Unavailable('locate');
  } finally {
    clearTimeout(timer);
    o.signal?.removeEventListener('abort', stop);
  }
}

/** Runs one direction; resolves when the test time is up (we close) or the server closes. */
function socketTest(url: string, o: Ndt7Options, onMessage: (ev: MessageEvent, elapsed: number) => void, onOpen: (ws: WebSocket, stopped: () => boolean) => void): Promise<{ elapsedMs: number }> {
  const WS = o.WebSocketImpl ?? WebSocket;
  const now = o.now ?? (() => performance.now());
  const duration = o.durationMs ?? 10_000;
  return new Promise((resolve, reject) => {
    let ws: WebSocket;
    try {
      ws = new WS(url, NDT7_SUBPROTOCOL);
    } catch {
      reject(new Ndt7Unavailable('socket'));
      return;
    }
    ws.binaryType = 'arraybuffer';
    let started: number | null = null;
    let done = false;
    let first = false;
    const finish = (error?: Error) => {
      if (done) return;
      done = true;
      clearTimeout(connectTimer);
      clearTimeout(endTimer);
      o.signal?.removeEventListener('abort', onAbort);
      try { ws.close(); } catch { /* already closed */ }
      if (error) reject(error);
      else resolve({ elapsedMs: started === null ? 0 : now() - started });
    };
    const onAbort = () => finish(new DOMException('stopped', 'AbortError'));
    o.signal?.addEventListener('abort', onAbort, { once: true });
    if (o.signal?.aborted) return onAbort();
    const connectTimer = setTimeout(() => { if (!first) finish(new Ndt7Unavailable('timeout')); }, o.connectMs ?? 6000);
    let endTimer: ReturnType<typeof setTimeout> | undefined;
    ws.onopen = () => {
      started = now();
      endTimer = setTimeout(() => finish(), duration);
      onOpen(ws, () => done);
    };
    ws.onmessage = (ev) => {
      first = true;
      if (started !== null) onMessage(ev, now() - started);
    };
    ws.onerror = () => finish(new Ndt7Unavailable('socket'));
    ws.onclose = () => finish(first || started !== null ? undefined : new Ndt7Unavailable('closed'));
  });
}

export async function runNdt7(o: Ndt7Options): Promise<Ndt7Result> {
  const target = await locate(o);
  // download: count what arrives
  let bytes = 0;
  const seen: { minRtt: number | null } = { minRtt: null };
  const d = await socketTest(
    target.download,
    o,
    (ev) => {
      if (typeof ev.data === 'string') {
        const m = parseServerMeasurement(ev.data);
        if (m?.minRttMs != null) seen.minRtt = m.minRttMs;
      } else bytes += (ev.data as ArrayBuffer).byteLength ?? (ev.data as Blob).size ?? 0;
    },
    () => undefined,
  );
  const download = mbps(bytes, d.elapsedMs);
  if (!download) throw new Ndt7Unavailable('no-data');
  let upload: number | null = null;
  if (o.upload) {
    try {
      upload = await runUpload(target.upload, o);
    } catch (e) {
      if (o.signal?.aborted) throw e;
      upload = null; // download stands; the screen says upload could not be measured
    }
  }
  return { download, upload, minRtt: seen.minRtt, site: target.site };
}

/** Upload: send binary messages (8 KiB, doubling as the socket keeps up); the server's own count is the answer. */
async function runUpload(url: string, o: Ndt7Options): Promise<number> {
  const holder: { last: ServerMeasurement | null } = { last: null };
  let sent = 0;
  const MAX = 1 << 20;
  const buf = new Uint8Array(MAX);
  for (let n = 0; n < MAX; n += 65536) crypto.getRandomValues(buf.subarray(n, n + 65536));
  const r = await socketTest(
    url,
    o,
    (ev) => { holder.last = parseServerMeasurement(ev.data) ?? holder.last; },
    (ws, stopped) => {
      let size = 1 << 13;
      void (async () => {
        while (!stopped() && ws.readyState === 1) {
          if (ws.bufferedAmount < 7 * size) {
            ws.send(buf.subarray(0, size));
            sent += size;
            if (size < MAX && size < sent / 16) size = Math.min(MAX, size * 2);
            await wait(0);
          } else await wait(5);
        }
      })();
    },
  );
  const fromServer = uploadMbpsFromServer(holder.last);
  if (fromServer) return fromServer;
  // no server count: what we sent less what was still queued, over the time
  const v = mbps(sent, r.elapsedMs);
  if (!v) throw new Ndt7Unavailable('no-data');
  return v;
}
